import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaTrash,
  FaList,
  FaBox,
  FaTags,
  FaShoppingCart,
  FaImage,
  FaCheck,
  FaTimes,
  FaSearch,
  FaPrint,
  FaCheckCircle,
  FaExclamationTriangle,
  FaEllipsisV,
  FaEdit
} from "react-icons/fa";
import { API_BASE_URL } from "../config";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard"); // dashboard, products, banners, orders
  
  // Data States
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [orders, setOrders] = useState([]);
  const [searchOrder, setSearchOrder] = useState("");
  
  // Add Form States
  const [productName, setProductName] = useState("");
  const [subProductForm, setSubProductForm] = useState({
    pro_id: "",
    sub_name: "",
    trend: 0,
    childimg: null
  });
  const [rateForm, setRateForm] = useState({
    sub_id: "",
    quantity: "",
    rate: "",
    Discount: "",
    stocks: 100
  });
  const [bannerForm, setBannerForm] = useState({
    title: "",
    description: "",
    active: 1,
    image: null
  });
  
  // Inline Stock Edit State
  const [editingStockId, setEditingStockId] = useState(null);
  const [tempStockValue, setTempStockValue] = useState("");

  // Edit Dialog States
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingSubproduct, setEditingSubproduct] = useState(null);
  const [editingRate, setEditingRate] = useState(null);
  const [editingBanner, setEditingBanner] = useState(null);

  // Delivery Regions State
  const [deliveryRegions, setDeliveryRegions] = useState({});
  const [newRegionName, setNewRegionName] = useState("");
  const [newRegionAmount, setNewRegionAmount] = useState("");

  // Feedback States
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const getAuthHeaders = (contentType = "application/json") => {
    const token = localStorage.getItem("token");
    const headers = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    if (contentType) {
      headers["Content-Type"] = contentType;
    }
    return headers;
  };

  // Fetch all data on mount
  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    if (!savedUser || !token) {
      navigate("/loginRegistor");
      return;
    }
    try {
      const u = JSON.parse(savedUser);
      if (u.role !== "admin") {
        navigate("/");
        return;
      }
    } catch (e) {
      navigate("/loginRegistor");
      return;
    }
    fetchAllData();
    fetchOrderDetails();
    fetchBanners();
  }, []);

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 4000);
  };

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(""), 4000);
  };

  const fetchAllData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/all-data`);
      const data = await response.json();
      if (response.ok) {
        setProducts(data);
      } else {
        showError(data.error || "Failed to fetch products");
      }
    } catch (err) {
      showError("Error fetching product data from backend");
    }
  };

  const fetchOrderDetails = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/order-details`, {
        headers: getAuthHeaders(null)
      });
      const data = await response.json();
      if (response.ok) {
        setOrders(data);
      } else {
        showError(data.error || "Failed to fetch orders");
      }
    } catch (err) {
      showError("Error fetching orders from backend");
    }
  };

  const fetchDeliveryRegions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/delivery-regions`);
      const data = await response.json();
      if (response.ok && data) {
        setDeliveryRegions(data);
      }
    } catch (err) {
      console.log("Error fetching delivery regions:", err);
    }
  };

  const handleAddRegion = () => {
    if (!newRegionName.trim() || !newRegionAmount) {
      showError("Please enter region name and minimum amount");
      return;
    }
    const amount = parseFloat(newRegionAmount);
    if (isNaN(amount) || amount <= 0) {
      showError("Please enter a valid minimum amount");
      return;
    }
    setDeliveryRegions(prev => ({
      ...prev,
      [newRegionName.trim()]: amount
    }));
    setNewRegionName("");
    setNewRegionAmount("");
    showSuccess("Region added to list. Click 'Save All Regions' to persist.");
  };

  const handleRemoveRegion = (regionName) => {
    const updated = { ...deliveryRegions };
    delete updated[regionName];
    setDeliveryRegions(updated);
    showSuccess("Region removed. Click 'Save All Regions' to persist.");
  };

  const handleSaveRegions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/delivery-regions/save`, {
        method: "POST",
        headers: getAuthHeaders("application/json"),
        body: JSON.stringify(deliveryRegions)
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Delivery regions saved successfully! Cart will use these values.");
      } else {
        showError(data.error || "Failed to save delivery regions");
      }
    } catch (err) {
      showError("Error saving delivery regions");
    }
  };

  const fetchBanners = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/banners/all`, {
        headers: getAuthHeaders(null)
      });
      const data = await response.json();
      if (response.ok) {
        setBanners(data);
      } else {
        showError(data.error || "Failed to fetch banners");
      }
    } catch (err) {
      showError("Error fetching banners from backend");
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    if (!productName.trim()) return;
    try {
      const response = await fetch(`${API_BASE_URL}/add-product`, {
        method: "POST",
        headers: getAuthHeaders("application/x-www-form-urlencoded"),
        body: new URLSearchParams({ name: productName })
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Product category added successfully");
        setProductName("");
        fetchAllData();
      } else {
        showError(data.error || "Failed to add product");
      }
    } catch (err) {
      showError("Error adding product");
    }
  };

  const handleAddSubProduct = async (e) => {
    e.preventDefault();
    if (!subProductForm.pro_id || !subProductForm.sub_name) {
      showError("Please fill in the product category and subproduct name");
      return;
    }
    const formData = new FormData();
    formData.append("pro_id", subProductForm.pro_id);
    formData.append("sub_name", subProductForm.sub_name);
    formData.append("trend", subProductForm.trend);
    if (subProductForm.childimg) {
      formData.append("childimg", subProductForm.childimg);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/add-subproduct`, {
        method: "POST",
        headers: getAuthHeaders(null),
        body: formData
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Subproduct added successfully");
        setSubProductForm({ pro_id: "", sub_name: "", trend: 0, childimg: null });
        fetchAllData();
      } else {
        showError(data.error || "Failed to add subproduct");
      }
    } catch (err) {
      showError("Error adding subproduct");
    }
  };

  const handleAddRate = async (e) => {
    e.preventDefault();
    if (!rateForm.sub_id || !rateForm.quantity || !rateForm.rate) {
      showError("Subproduct, quantity, and rate are required");
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/add-rate`, {
        method: "POST",
        headers: getAuthHeaders("application/json"),
        body: JSON.stringify(rateForm)
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Rate entity successfully added");
        setRateForm({ sub_id: "", quantity: "", rate: "", Discount: "", stocks: 100 });
        fetchAllData();
      } else {
        showError(data.error || "Failed to add rate");
      }
    } catch (err) {
      showError("Error adding rate");
    }
  };

  const handleDeleteSubProduct = async (sub_id) => {
    if (!window.confirm("Are you sure you want to delete this subproduct? This will delete all its rates.")) return;
    try {
      const response = await fetch(`${API_BASE_URL}/delete-subproduct/${sub_id}`, {
        method: "DELETE",
        headers: getAuthHeaders(null)
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess(data.message || "Subproduct deleted successfully");
        fetchAllData();
      } else {
        showError(data.error || "Failed to delete subproduct");
      }
    } catch (err) {
      showError("Error deleting subproduct");
    }
  };

  const handleUpdateStockInline = async (rate_id) => {
    const numericStock = parseFloat(tempStockValue);
    if (isNaN(numericStock) || numericStock < 0) {
      showError("Please enter a valid stock number");
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/update-stock`, {
        method: "POST",
        headers: getAuthHeaders("application/json"),
        body: JSON.stringify({ rate_id, stocks: numericStock })
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Stock level updated successfully!");
        setEditingStockId(null);
        fetchAllData();
      } else {
        showError(data.error || "Failed to update stock");
      }
    } catch (err) {
      showError("Error updating stock");
    }
  };

  const handleEditProduct = async (e) => {
    e.preventDefault();
    if (!editingProduct || !editingProduct.name.trim()) return;
    try {
      const response = await fetch(`${API_BASE_URL}/edit-product/${editingProduct.pro_id}`, {
        method: "PUT",
        headers: getAuthHeaders("application/json"),
        body: JSON.stringify({ name: editingProduct.name })
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Product category updated successfully");
        setEditingProduct(null);
        fetchAllData();
      } else {
        showError(data.error || "Failed to update category");
      }
    } catch (err) {
      showError("Error updating product category");
    }
  };

  const handleDeleteProductCategory = async (pro_id) => {
    if (!window.confirm("Are you sure you want to delete this category? This will delete all its subproducts and rates!")) return;
    try {
      const response = await fetch(`${API_BASE_URL}/delete-product/${pro_id}`, {
        method: "DELETE",
        headers: getAuthHeaders(null)
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Product category deleted successfully");
        fetchAllData();
      } else {
        showError(data.error || "Failed to delete category");
      }
    } catch (err) {
      showError("Error deleting product category");
    }
  };

  const handleEditSubproductSubmit = async (e) => {
    e.preventDefault();
    if (!editingSubproduct || !editingSubproduct.subName.trim()) return;
    const formData = new FormData();
    formData.append("sub_name", editingSubproduct.subName);
    formData.append("trend", editingSubproduct.trend);
    if (editingSubproduct.newChildimg) {
      formData.append("childimg", editingSubproduct.newChildimg);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/edit-subproduct/${editingSubproduct.sub_id}`, {
        method: "PUT",
        headers: getAuthHeaders(null),
        body: formData
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Subproduct updated successfully");
        setEditingSubproduct(null);
        fetchAllData();
      } else {
        showError(data.error || "Failed to update subproduct");
      }
    } catch (err) {
      showError("Error updating subproduct");
    }
  };

  const handleEditRateSubmit = async (e) => {
    e.preventDefault();
    if (!editingRate || !editingRate.quantity || !editingRate.rate) return;
    try {
      const response = await fetch(`${API_BASE_URL}/edit-rate/${editingRate.rate_id}`, {
        method: "PUT",
        headers: getAuthHeaders("application/json"),
        body: JSON.stringify({
          quantity: editingRate.quantity,
          rate: editingRate.rate,
          Discount: editingRate.Discount,
          stocks: editingRate.stocks
        })
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Rate entity updated successfully");
        setEditingRate(null);
        fetchAllData();
      } else {
        showError(data.error || "Failed to update rate entity");
      }
    } catch (err) {
      showError("Error updating rate entity");
    }
  };

  const handleDeleteRateEntity = async (rate_id) => {
    if (!window.confirm("Are you sure you want to delete this pricing rate?")) return;
    try {
      const response = await fetch(`${API_BASE_URL}/delete-rate/${rate_id}`, {
        method: "DELETE",
        headers: getAuthHeaders(null)
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Rate deleted successfully");
        fetchAllData();
      } else {
        showError(data.error || "Failed to delete rate");
      }
    } catch (err) {
      showError("Error deleting rate");
    }
  };

  const handleEditBannerSubmit = async (e) => {
    e.preventDefault();
    if (!editingBanner || !editingBanner.title.trim()) return;
    const formData = new FormData();
    formData.append("title", editingBanner.title);
    formData.append("description", editingBanner.description || "");
    formData.append("active", editingBanner.active);
    if (editingBanner.newImage) {
      formData.append("image", editingBanner.newImage);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/banners/edit/${editingBanner.banner_id}`, {
        method: "PUT",
        headers: getAuthHeaders(null),
        body: formData
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Banner updated successfully");
        setEditingBanner(null);
        fetchBanners();
      } else {
        showError(data.error || "Failed to update banner");
      }
    } catch (err) {
      showError("Error updating banner");
    }
  };

  const handleAddBanner = async (e) => {
    e.preventDefault();
    if (!bannerForm.image) {
      showError("Please select a banner image");
      return;
    }
    const formData = new FormData();
    formData.append("title", bannerForm.title);
    formData.append("description", bannerForm.description);
    formData.append("active", bannerForm.active);
    formData.append("image", bannerForm.image);

    try {
      const response = await fetch(`${API_BASE_URL}/banners/add`, {
        method: "POST",
        headers: getAuthHeaders(null),
        body: formData
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Banner added successfully");
        setBannerForm({ title: "", description: "", active: 1, image: null });
        // Clear file input manually
        const fileInput = document.getElementById("bannerImageFile");
        if (fileInput) fileInput.value = "";
        fetchBanners();
      } else {
        showError(data.error || "Failed to add banner");
      }
    } catch (err) {
      showError("Error adding banner");
    }
  };

  const handleDeleteBanner = async (banner_id) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) return;
    try {
      const response = await fetch(`${API_BASE_URL}/banners/delete/${banner_id}`, {
        method: "DELETE",
        headers: getAuthHeaders(null)
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Banner deleted successfully");
        fetchBanners();
      } else {
        showError(data.error || "Failed to delete banner");
      }
    } catch (err) {
      showError("Error deleting banner");
    }
  };

  const handleToggleBanner = async (banner_id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/banners/toggle/${banner_id}`, {
        method: "PUT",
        headers: getAuthHeaders(null)
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Banner visibility toggled");
        fetchBanners();
      } else {
        showError(data.error || "Failed to toggle banner");
      }
    } catch (err) {
      showError("Error toggling banner visibility");
    }
  };

  const handleUpdateOrderStatus = async (order_id, status) => {
    try {
      const response = await fetch(`${API_BASE_URL}/update-order-status`, {
        method: "POST",
        headers: getAuthHeaders("application/json"),
        body: JSON.stringify({ order_id, status })
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Order status updated!");
        fetchOrderDetails();
      } else {
        showError(data.error || "Failed to update order status");
      }
    } catch (err) {
      showError("Error updating order status");
    }
  };

  const handleUpdatePaymentStatus = async (order_id, payment_status) => {
    try {
      const response = await fetch(`${API_BASE_URL}/update-payment-status`, {
        method: "POST",
        headers: getAuthHeaders("application/json"),
        body: JSON.stringify({ order_id, payment_status })
      });
      const data = await response.json();
      if (response.ok) {
        showSuccess("Payment status updated!");
        fetchOrderDetails();
      } else {
        showError(data.error || "Failed to update payment status");
      }
    } catch (err) {
      showError("Error updating payment status");
    }
  };

  const handlePrintInvoice = (order) => {
    const printWindow = window.open("", "_blank");
    const cartItemsHtml = order.cart_data && Array.isArray(order.cart_data) 
      ? order.cart_data.map(item => `
          <tr>
            <td style="border: 1px solid #ddd; padding: 8px;">${item.name}</td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${item.quantity}</td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">₹${item.rate}</td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">₹${(item.quantity * item.rate).toFixed(2)}</td>
          </tr>
        `).join("")
      : "<tr><td colspan='4'>No items in order</td></tr>";

    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice - Order #${order.order_id || order.id}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #333; margin: 20px; }
            .header { border-bottom: 2px solid #800000; padding-bottom: 10px; margin-bottom: 20px; }
            .invoice-title { font-size: 24px; font-weight: bold; color: #800000; }
            .info-grid { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .info-block { width: 48%; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th { background-color: #800000; color: white; padding: 10px; text-align: left; }
            .total-row { font-weight: bold; font-size: 16px; background-color: #f9f9f9; }
            .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #777; border-top: 1px solid #ddd; padding-top: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="invoice-title">Crackers E-Commerce Invoice</div>
            <div>Order ID: #${order.order_id || order.id}</div>
          </div>
          <div class="info-grid">
            <div class="info-block">
              <h3>Customer Details</h3>
              <p><strong>Name:</strong> ${order.login_name || order.LOGIN_NAME}</p>
              <p><strong>Phone:</strong> ${order.login_phone || order.LOGIN_PHONE}</p>
              <p><strong>Email:</strong> ${order.login_email || order.LOGIN_EMAIL}</p>
            </div>
            <div class="info-block" style="text-align: right;">
              <h3>Delivery Details</h3>
              <p><strong>Address:</strong> ${order.login_address || order.LOGIN_ADDRESS}</p>
              <p><strong>Date:</strong> ${new Date(order.order_date || order.ORDER_DATE).toLocaleDateString()}</p>
              <p><strong>Payment Method:</strong> ${order.payment_method || order.PAYMENT_METHOD || "COD"}</p>
              <p><strong>Payment Status:</strong> ${order.payment_status || order.PAYMENT_STATUS || "Pending"}</p>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Item Description</th>
                <th style="text-align: center;">Qty</th>
                <th style="text-align: right;">Unit Price</th>
                <th style="text-align: right;">Sub Total</th>
              </tr>
            </thead>
            <tbody>
              ${cartItemsHtml}
              <tr class="total-row">
                <td colspan="3" style="border: 1px solid #ddd; padding: 8px; text-align: right;">Grand Total:</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: right; color: #800000;">₹${order.total || order.TOTAL}</td>
              </tr>
            </tbody>
          </table>
          <div class="footer">
            <p>Thank you for shopping with us! Wishing you a secure & colorful festival.</p>
            <p>Powered by SmartGoNext Systems</p>
          </div>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // KPI Calculations
  const totalRevenue = orders
    .filter(o => (o.status || o.STATUS) !== "Cancelled")
    .reduce((acc, curr) => acc + parseFloat(curr.total || curr.TOTAL || 0), 0);

  const outOfStockItems = products.flatMap(p => 
    (p.subProducts || p.subproducts || []).flatMap(s => 
      (s.rateEntities || s.rateentities || []).filter(r => parseFloat(r.stocks || r.stock || 0) <= 0)
    )
  ).length;

  const totalProductsCount = products.flatMap(p => p.subProducts || p.subproducts || []).length;

  // Search Filter for Orders
  const filteredOrders = orders.filter(o => {
    const q = searchOrder.toLowerCase();
    const idStr = String(o.order_id || o.id || "").toLowerCase();
    const nameStr = String(o.login_name || o.LOGIN_NAME || "").toLowerCase();
    const phoneStr = String(o.login_phone || o.LOGIN_PHONE || "").toLowerCase();
    const emailStr = String(o.login_email || o.LOGIN_EMAIL || "").toLowerCase();
    return idStr.includes(q) || nameStr.includes(q) || phoneStr.includes(q) || emailStr.includes(q);
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row text-gray-800">
      
      {/* Sidebar Navigation */}
      <div className="w-full md:w-64 bg-gradient-to-b from-red-950 via-red-900 to-red-950 text-white flex flex-col shadow-xl z-20">
        <div className="p-6 border-b border-red-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-3xl">✨</span>
            <div>
              <h2 className="font-extrabold text-lg bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text text-transparent">Crackers</h2>
              <p className="text-xs text-red-300">Admin Control Panel</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          <button
            onClick={() => setActiveTab("dashboard")}
            className={`w-full text-left py-3 px-4 rounded-xl flex items-center gap-3 font-semibold transition ${
              activeTab === "dashboard" ? "bg-red-800 text-yellow-300 shadow-md" : "hover:bg-red-800/40 text-red-100"
            }`}
          >
            📊 <span className="text-sm">Dashboard Overview</span>
          </button>
          <button
            onClick={() => setActiveTab("products")}
            className={`w-full text-left py-3 px-4 rounded-xl flex items-center gap-3 font-semibold transition ${
              activeTab === "products" ? "bg-red-800 text-yellow-300 shadow-md" : "hover:bg-red-800/40 text-red-100"
            }`}
          >
            📦 <span className="text-sm">Inventory & Stock</span>
          </button>
          <button
            onClick={() => setActiveTab("banners")}
            className={`w-full text-left py-3 px-4 rounded-xl flex items-center gap-3 font-semibold transition ${
              activeTab === "banners" ? "bg-red-800 text-yellow-300 shadow-md" : "hover:bg-red-800/40 text-red-100"
            }`}
          >
            🖼️ <span className="text-sm">Homepage Slideshow</span>
          </button>
          <button
            onClick={() => setActiveTab("orders")}
            className={`w-full text-left py-3 px-4 rounded-xl flex items-center gap-3 font-semibold transition ${
              activeTab === "orders" ? "bg-red-800 text-yellow-300 shadow-md" : "hover:bg-red-800/40 text-red-100"
            }`}
          >
            🛒 <span className="text-sm">Orders Management</span>
          </button>
          <button
            onClick={() => setActiveTab("delivery")}
            className={`w-full text-left py-3 px-4 rounded-xl flex items-center gap-3 font-semibold transition ${
              activeTab === "delivery" ? "bg-red-800 text-yellow-300 shadow-md" : "hover:bg-red-800/40 text-red-100"
            }`}
          >
            🚚 <span className="text-sm">Delivery Regions</span>
          </button>
        </nav>
        <div className="p-4 border-t border-red-800">
          <button
            onClick={() => {
              localStorage.removeItem("user");
              localStorage.removeItem("token");
              navigate("/");
            }}
            className="w-full bg-red-950/60 hover:bg-red-700/60 text-white hover:text-yellow-300 text-sm font-semibold py-2.5 rounded-xl border border-red-800/60 transition"
          >
            🚪 Logout Dashboard
          </button>
        </div>
      </div>

      {/* Main Panel Content Area */}
      <div className="flex-1 p-6 sm:p-10 overflow-y-auto">
        
        {/* Banner Alert Feedback */}
        {success && (
          <div className="bg-green-550 border border-green-500 text-green-900 bg-green-50 px-4 py-3 rounded-xl shadow-sm mb-6 flex items-center gap-3 animate-fade-in">
            <FaCheckCircle className="text-green-600 text-lg flex-shrink-0" />
            <p className="font-semibold text-sm">{success}</p>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-900 px-4 py-3 rounded-xl shadow-sm mb-6 flex items-center gap-3 animate-fade-in">
            <FaExclamationTriangle className="text-red-600 text-lg flex-shrink-0" />
            <p className="font-semibold text-sm">{error}</p>
          </div>
        )}

        {/* Dashboard Tab Content */}
        {activeTab === "dashboard" && (
          <div className="space-y-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-red-950">Overview Statistics</h1>
                <p className="text-sm text-gray-500">Live operational indicators for SmartGoNext platform.</p>
              </div>
            </div>
            
            {/* KPI Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 hover:shadow-md transition">
                <div className="bg-red-50 p-4 rounded-xl text-red-900 text-2xl">💰</div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-semibold">Total Revenue</p>
                  <p className="text-2xl font-extrabold text-red-950">₹{totalRevenue.toLocaleString()}</p>
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 hover:shadow-md transition">
                <div className="bg-orange-50 p-4 rounded-xl text-orange-600 text-2xl">📦</div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-semibold">Unique Items</p>
                  <p className="text-2xl font-extrabold text-red-950">{totalProductsCount}</p>
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 hover:shadow-md transition">
                <div className="bg-blue-50 p-4 rounded-xl text-blue-600 text-2xl">🛒</div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-semibold">Total Orders</p>
                  <p className="text-2xl font-extrabold text-red-950">{orders.length}</p>
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 hover:shadow-md transition">
                <div className="bg-rose-50 p-4 rounded-xl text-rose-600 text-2xl">⚠️</div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-semibold">Stock Alerts</p>
                  <p className={`text-2xl font-extrabold ${outOfStockItems > 0 ? "text-red-600" : "text-green-600"}`}>
                    {outOfStockItems} Out
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Access Block */}
            <div className="bg-gradient-to-r from-red-900 to-red-800 text-white rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="space-y-1">
                <h3 className="text-xl font-bold">Configure Banners or Manage Stock levels?</h3>
                <p className="text-sm text-red-100/90">Instantly update what customers see on the web storefront.</p>
              </div>
              <div className="flex gap-3">
                <button 
                  onClick={() => setActiveTab("products")} 
                  className="bg-white text-red-900 font-bold px-6 py-2.5 rounded-xl hover:bg-yellow-300 hover:text-red-950 transition text-sm"
                >
                  Manage Stock
                </button>
                <button 
                  onClick={() => setActiveTab("banners")} 
                  className="bg-red-950 text-white font-bold px-6 py-2.5 rounded-xl hover:bg-red-900 border border-red-700 hover:border-red-600 transition text-sm"
                >
                  Update Banner Slider
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Products & Stock Management Tab */}
        {activeTab === "products" && (
          <div className="space-y-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-red-955 text-red-950">Inventory & Stock Controls</h1>
              <p className="text-sm text-gray-500">Insert category groups, products, sub-rates, and maintain real-time warehouse count.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              
              {/* Product Insertion Form */}
              <div className="lg:col-span-1 space-y-6">
                
                {/* 1. Add Category */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
                  <h3 className="font-bold text-red-955 flex items-center gap-2 text-red-950">
                    <FaBox className="text-red-800" /> Create Product Category
                  </h3>
                  <form onSubmit={handleAddProduct} className="flex gap-2">
                    <input
                      type="text"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      placeholder="e.g. Sparklers, Bomb"
                      className="flex-1 px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                    />
                    <button type="submit" className="bg-red-900 hover:bg-red-800 text-white px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-1 transition">
                      <FaPlus /> Add
                    </button>
                  </form>
                </div>

                {/* 2. Add Subproduct */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
                  <h3 className="font-bold text-red-955 flex items-center gap-2 text-red-950">
                    <FaBox className="text-red-800" /> Create Subproduct Item
                  </h3>
                  <form onSubmit={handleAddSubProduct} className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Parent Product Category</label>
                      <select
                        value={subProductForm.pro_id}
                        onChange={(e) => setSubProductForm({ ...subProductForm, pro_id: e.target.value })}
                        className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900 bg-white"
                      >
                        <option value="">Select Category</option>
                        {products.map((p) => (
                          <option key={p.pro_id} value={p.pro_id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Subproduct Name</label>
                      <input
                        type="text"
                        value={subProductForm.sub_name}
                        onChange={(e) => setSubProductForm({ ...subProductForm, sub_name: e.target.value })}
                        placeholder="e.g. 10 Pcs Sparkler, Red Fort Bomb"
                        className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Dazzle/Trend rating (0-5)</label>
                      <input
                        type="number"
                        min="0"
                        max="5"
                        value={subProductForm.trend}
                        onChange={(e) => setSubProductForm({ ...subProductForm, trend: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Product Image File</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setSubProductForm({ ...subProductForm, childimg: e.target.files[0] })}
                        className="w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-red-50 file:text-red-900 hover:file:bg-red-100"
                      />
                    </div>
                    <button type="submit" className="w-full bg-red-900 hover:bg-red-800 text-white py-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-1 transition mt-2">
                      <FaPlus /> Create Item
                    </button>
                  </form>
                </div>

                {/* 3. Add Rate Entity */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
                  <h3 className="font-bold text-red-955 flex items-center gap-2 text-red-950">
                    <FaTags className="text-red-800" /> Create Price & Rate Entity
                  </h3>
                  <form onSubmit={handleAddRate} className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Link to Subproduct Item</label>
                      <select
                        value={rateForm.sub_id}
                        onChange={(e) => setRateForm({ ...rateForm, sub_id: e.target.value })}
                        className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900 bg-white"
                      >
                        <option value="">Select Subproduct</option>
                        {products.flatMap(p => 
                          (p.subProducts || p.subproducts || []).map(s => (
                            <option key={s.sub_id} value={s.sub_id}>{s.subName}</option>
                          ))
                        )}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Packaging Quantity Details</label>
                      <input
                        type="text"
                        value={rateForm.quantity}
                        onChange={(e) => setRateForm({ ...rateForm, quantity: e.target.value })}
                        placeholder="e.g. 1 Box (5 pieces), 1 Packet"
                        className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Price (₹)</label>
                        <input
                          type="text"
                          value={rateForm.rate}
                          onChange={(e) => setRateForm({ ...rateForm, rate: e.target.value })}
                          placeholder="Rate"
                          className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Discount (%)</label>
                        <input
                          type="text"
                          value={rateForm.Discount}
                          onChange={(e) => setRateForm({ ...rateForm, Discount: e.target.value })}
                          placeholder="Discount"
                          className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Initial Stock Level</label>
                      <input
                        type="number"
                        value={rateForm.stocks}
                        onChange={(e) => setRateForm({ ...rateForm, stocks: parseFloat(e.target.value) || 0 })}
                        className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                      />
                    </div>
                    <button type="submit" className="w-full bg-red-900 hover:bg-red-800 text-white py-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-1 transition mt-2">
                      <FaPlus /> Add Pricing
                    </button>
                  </form>
                </div>
              </div>

              {/* Products List & Inline Stock Maintenance */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                  <h3 className="text-lg font-bold text-red-950 mb-6 flex items-center gap-2">
                    <FaList className="text-red-800" /> Active Inventory List
                  </h3>
                  
                  <div className="space-y-6">
                    {products.map((category) => (
                      <div key={category.pro_id} className="border border-gray-100 rounded-xl p-4 space-y-3 bg-gray-50/50">
                        <div className="flex justify-between items-center border-b pb-2 border-gray-200/60">
                          <div className="flex items-center gap-2">
                            <h4 className="font-extrabold text-red-900 text-base">{category.name}</h4>
                            <button
                              onClick={() => setEditingProduct({ pro_id: category.pro_id, name: category.name })}
                              className="text-gray-400 hover:text-red-950 p-1"
                              title="Edit Category Name"
                            >
                              <FaEdit size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteProductCategory(category.pro_id)}
                              className="text-red-300 hover:text-red-600 p-1"
                              title="Delete Category"
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                          <span className="text-xs font-semibold bg-red-50 text-red-900 px-2.5 py-0.5 rounded-full">
                            {(category.subProducts || category.subproducts || []).length} items
                          </span>
                        </div>
                        
                        <div className="space-y-3">
                          {!(category.subProducts || category.subproducts || []).length && (
                            <p className="text-xs text-gray-400 italic">No subproducts in this category.</p>
                          )}
                          {(category.subProducts || category.subproducts || []).map((sub) => (
                            <div key={sub.sub_id} className="bg-white p-4 rounded-xl border border-gray-100 flex flex-col sm:flex-row justify-between gap-4">
                              <div className="flex gap-4">
                                {sub.childimg && (
                                  <img
                                    src={sub.childimg}
                                    alt={sub.subName}
                                    className="w-16 h-16 object-cover rounded-xl border"
                                  />
                                )}
                                <div>
                                  <p className="font-bold text-gray-800 text-sm">{sub.subName}</p>
                                  <p className="text-xs text-orange-500 font-semibold mb-1">Dazzle: {Array(sub.trend || 0).fill("★").join("") || "None"}</p>
                                  
                                  {/* Rates Details & Stock Levels */}
                                  <div className="space-y-2 mt-2">
                                    {(sub.rateEntities || sub.rateentities || []).map((rate) => {
                                      const stockVal = parseFloat(rate.stocks !== undefined ? rate.stocks : (rate.stock !== undefined ? rate.stock : 100));
                                      const isLowStock = stockVal <= 10 && stockVal > 0;
                                      const isOut = stockVal <= 0;
                                      
                                      return (
                                        <div key={rate.rate_id} className="text-xs text-gray-600 bg-gray-50 px-2.5 py-1.5 rounded-lg flex flex-wrap items-center gap-2 border">
                                          <div className="flex items-center gap-1.5">
                                            <span><strong>{rate.quantity}</strong>: ₹{rate.rate} (Disc: {rate.Discount || rate.discount || 0}%)</span>
                                            <button
                                              onClick={() => setEditingRate({
                                                rate_id: rate.rate_id,
                                                quantity: rate.quantity,
                                                rate: rate.rate,
                                                Discount: rate.Discount || rate.discount || 0,
                                                stocks: stockVal
                                              })}
                                              className="text-gray-400 hover:text-red-950 p-0.5"
                                              title="Edit Price/Discount Details"
                                            >
                                              <FaEdit size={10} />
                                            </button>
                                            <button
                                              onClick={() => handleDeleteRateEntity(rate.rate_id)}
                                              className="text-red-300 hover:text-red-600 p-0.5"
                                              title="Delete Rate Detail"
                                            >
                                              <FaTrash size={10} />
                                            </button>
                                          </div>
                                          
                                          {/* Stock badge & Inline editor toggle */}
                                          <div className="ml-auto flex items-center gap-2">
                                            {editingStockId === rate.rate_id ? (
                                              <div className="flex items-center gap-1.5">
                                                <input
                                                  type="number"
                                                  value={tempStockValue}
                                                  onChange={(e) => setTempStockValue(e.target.value)}
                                                  className="w-16 border rounded px-1.5 py-0.5 text-center text-xs"
                                                  placeholder="Stock"
                                                  autoFocus
                                                />
                                                <button
                                                  onClick={() => handleUpdateStockInline(rate.rate_id)}
                                                  className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                                                  title="Save Stock"
                                                >
                                                  <FaCheck />
                                                </button>
                                                <button
                                                  onClick={() => setEditingStockId(null)}
                                                  className="bg-gray-400 hover:bg-gray-500 text-white px-2 py-1 rounded text-xs"
                                                  title="Cancel"
                                                >
                                                  <FaTimes />
                                                </button>
                                              </div>
                                            ) : (
                                              <div className="flex items-center gap-1.5">
                                                <span className={`px-2 py-0.5 rounded font-bold ${
                                                  isOut ? "bg-red-100 text-red-700" :
                                                  isLowStock ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"
                                                }`}>
                                                  Stock: {stockVal}
                                                </span>
                                                <button
                                                  onClick={() => {
                                                    setEditingStockId(rate.rate_id);
                                                    setTempStockValue(String(stockVal));
                                                  }}
                                                  className="text-gray-400 hover:text-red-900 p-1"
                                                  title="Edit Stock"
                                                >
                                                  <FaEdit />
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="sm:self-start flex gap-1">
                                <button
                                  onClick={() => setEditingSubproduct({
                                    sub_id: sub.sub_id,
                                    subName: sub.subName,
                                    trend: sub.trend || 0,
                                    childimg: sub.childimg
                                  })}
                                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-2.5 rounded-xl border transition"
                                  title="Edit Subproduct Details"
                                >
                                  <FaEdit className="text-sm" />
                                </button>
                                <button
                                  onClick={() => handleDeleteSubProduct(sub.sub_id)}
                                  className="bg-red-50 hover:bg-red-100 text-red-600 p-2.5 rounded-xl border border-red-100 transition"
                                  title="Delete Subproduct"
                                >
                                  <FaTrash className="text-sm" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Homepage Banner Slideshow Manager Tab */}
        {activeTab === "banners" && (
          <div className="space-y-8 animate-fade-in">
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-red-955 text-red-950">Homepage Banner Slideshow</h1>
              <p className="text-sm text-gray-500">Add dynamic promotional images, configure descriptions, and toggle active slide visibility instantly.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              
              {/* Add Banner Form */}
              <div className="lg:col-span-1">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
                  <h3 className="font-bold text-red-955 flex items-center gap-2 text-red-950">
                    <FaImage className="text-red-800" /> Add New Banner Slide
                  </h3>
                  <form onSubmit={handleAddBanner} className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Banner Title *</label>
                      <input
                        type="text"
                        value={bannerForm.title}
                        onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })}
                        placeholder="e.g. Light Up Diwali with Sparkle!"
                        className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Description / Subtitle</label>
                      <textarea
                        value={bannerForm.description}
                        onChange={(e) => setBannerForm({ ...bannerForm, description: e.target.value })}
                        placeholder="e.g. Celebrate the festival of lights with our premium eco-friendly crackers."
                        className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900 h-20"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Banner Slide Image *</label>
                      <input
                        type="file"
                        id="bannerImageFile"
                        accept="image/*"
                        onChange={(e) => setBannerForm({ ...bannerForm, image: e.target.files[0] })}
                        className="w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-red-50 file:text-red-900 hover:file:bg-red-100"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Active Visibility Status</label>
                      <select
                        value={bannerForm.active}
                        onChange={(e) => setBannerForm({ ...bannerForm, active: parseInt(e.target.value) || 1 })}
                        className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900 bg-white"
                      >
                        <option value="1">Active (Show on homepage)</option>
                        <option value="0">Inactive (Hide)</option>
                      </select>
                    </div>
                    
                    <button type="submit" className="w-full bg-red-900 hover:bg-red-800 text-white py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1 transition shadow-md">
                      <FaPlus /> Upload & Add Banner
                    </button>
                  </form>
                </div>
              </div>

              {/* Banner Items grid */}
              <div className="lg:col-span-2">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                  <h3 className="text-lg font-bold text-red-950 mb-6 flex items-center gap-2">
                    <FaImage className="text-red-800" /> Current Store Banners ({banners.length})
                  </h3>
                  
                  {banners.length === 0 ? (
                    <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed">
                      <p className="text-sm text-gray-500 font-semibold">No custom banners set up.</p>
                      <p className="text-xs text-gray-400">The storefront is currently showing the fallback local banner slides.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {banners.map((b) => {
                        const banner_id = b.banner_id || b.BANNER_ID;
                        const is_active = parseInt(b.active !== undefined ? b.active : b.ACTIVE) === 1;
                        const img_url = b.image_url || b.IMAGE_URL;
                        
                        return (
                          <div key={banner_id} className="border border-gray-150 rounded-2xl overflow-hidden flex flex-col bg-gray-55/40 bg-gray-50">
                            <div className="relative aspect-video bg-black/10">
                              <img
                                src={img_url}
                                alt={b.title || b.TITLE}
                                className="w-full h-full object-cover"
                              />
                              <span className={`absolute top-3 right-3 text-xs font-bold px-2.5 py-0.5 rounded-full shadow ${
                                is_active ? "bg-green-600 text-white" : "bg-gray-500 text-white"
                              }`}>
                                {is_active ? "Active" : "Inactive"}
                              </span>
                            </div>
                            <div className="p-4 flex-1 flex flex-col justify-between space-y-4">
                              <div>
                                <h4 className="font-bold text-sm text-gray-800 line-clamp-1">{b.title || b.TITLE}</h4>
                                <p className="text-xs text-gray-500 line-clamp-2 mt-1">{b.description || b.DESCRIPTION || "No description provided."}</p>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleToggleBanner(banner_id)}
                                  className={`flex-1 text-xs font-semibold py-1.5 rounded-lg border transition ${
                                    is_active ? "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300" : "bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                                  }`}
                                >
                                  {is_active ? "👁️ Hide" : "👁️ Activate"}
                                </button>
                                <button
                                  onClick={() => setEditingBanner({
                                    banner_id,
                                    title: b.title || b.TITLE,
                                    description: b.description || b.DESCRIPTION || "",
                                    active: is_active ? 1 : 0,
                                    image_url: img_url
                                  })}
                                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 p-2 rounded-lg transition"
                                  title="Edit Banner"
                                >
                                  <FaEdit className="text-xs" />
                                </button>
                                <button
                                  onClick={() => handleDeleteBanner(banner_id)}
                                  className="bg-red-50 hover:bg-red-100 text-red-600 border border-red-100 p-2 rounded-lg transition"
                                  title="Delete Banner"
                                >
                                  <FaTrash className="text-xs" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Orders Management Tab */}
        {activeTab === "orders" && (
          <div className="space-y-8 animate-fade-in">
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-red-955 text-red-950">Orders Tracker & Dispatch</h1>
              <p className="text-sm text-gray-500">Track pending transactions, change customer shipping progress, and generate bills.</p>
            </div>

            {/* Search filter and action block */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4 items-center">
              <div className="relative flex-1 w-full">
                <FaSearch className="absolute left-3 top-3.5 text-gray-400" />
                <input
                  type="text"
                  value={searchOrder}
                  onChange={(e) => setSearchOrder(e.target.value)}
                  placeholder="Search orders by ID, name, email or phone..."
                  className="w-full pl-10 pr-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                />
              </div>
              <button 
                onClick={fetchOrderDetails} 
                className="w-full md:w-auto bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-semibold px-5 py-2.5 rounded-xl border transition"
              >
                🔄 Refresh List
              </button>
            </div>

            {/* Orders list container */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gradient-to-r from-red-950 to-red-900 text-white text-xs font-semibold uppercase">
                      <th className="p-4">ID</th>
                      <th className="p-4">Customer Info</th>
                      <th className="p-4">Billing Address</th>
                      <th className="p-4">Cart Data</th>
                      <th className="p-4">Financials</th>
                      <th className="p-4">Status Adjustments</th>
                      <th className="p-4 text-center">Receipt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-150 text-sm">
                    {filteredOrders.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="p-8 text-center text-gray-500">No orders found matching the filter search.</td>
                      </tr>
                    ) : (
                      filteredOrders.map((order) => {
                        const order_id = order.order_id || order.ORDER_ID || order.id;
                        const login_name = order.login_name || order.LOGIN_NAME || "N/A";
                        const login_phone = order.login_phone || order.LOGIN_PHONE || "N/A";
                        const login_email = order.login_email || order.LOGIN_EMAIL || "N/A";
                        const login_address = order.login_address || order.LOGIN_ADDRESS || "N/A";
                        
                        const status_val = order.status || order.STATUS || "Pending";
                        const payment_method = order.payment_method || order.PAYMENT_METHOD || "COD";
                        const payment_status = order.payment_status || order.PAYMENT_STATUS || "Pending";
                        const transaction_id = order.transaction_id || order.TRANSACTION_ID || "";
                        const order_date = order.order_date || order.ORDER_DATE;
                        const total_amount = order.total || order.TOTAL || "0.00";
                        
                        return (
                          <tr key={order_id} className="hover:bg-gray-50/60 transition">
                            <td className="p-4 font-bold text-red-900">#{order_id}</td>
                            <td className="p-4 space-y-1">
                              <p className="font-bold text-gray-800">{login_name}</p>
                              <p className="text-xs text-gray-500">📞 {login_phone}</p>
                              <p className="text-xs text-gray-500">✉️ {login_email}</p>
                            </td>
                            <td className="p-4 max-w-xs truncate text-xs text-gray-600" title={login_address}>
                              {login_address}
                            </td>
                            <td className="p-4">
                              {order.cart_data && Array.isArray(order.cart_data) ? (
                                <ul className="space-y-1.5 max-h-24 overflow-y-auto pr-1">
                                  {order.cart_data.map((item, idx) => (
                                    <li key={idx} className="text-xs text-gray-600 flex justify-between gap-4">
                                      <span className="font-medium">{item.name || item.subName}</span>
                                      <span className="text-gray-400">x{item.quantity}</span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-xs text-gray-400 italic">No items recorded</span>
                              )}
                            </td>
                            <td className="p-4 space-y-1">
                              <p className="font-extrabold text-gray-800 text-sm">₹{total_amount}</p>
                              <span className="inline-block text-[10px] font-bold bg-red-50 text-red-900 px-2 py-0.5 rounded border border-red-100">
                                {payment_method}
                              </span>
                              {transaction_id && (
                                <p className="text-[10px] text-gray-400 font-mono">Tx: {transaction_id}</p>
                              )}
                            </td>
                            <td className="p-4 space-y-2">
                              {/* Order dispatch progress selector */}
                              <div>
                                <label className="block text-[10px] text-gray-400 font-semibold mb-0.5 uppercase">Dispatch Progress</label>
                                <select
                                  value={status_val}
                                  onChange={(e) => handleUpdateOrderStatus(order_id, e.target.value)}
                                  className={`w-32 px-2 py-1 text-xs border rounded-lg font-bold bg-white focus:outline-none focus:ring-1 focus:ring-red-900 ${
                                    status_val === "Delivered" ? "text-green-700" :
                                    status_val === "Dispatched" ? "text-blue-700" :
                                    status_val === "Cancelled" ? "text-red-700" : "text-orange-700"
                                  }`}
                                >
                                  <option value="Pending">Pending</option>
                                  <option value="Dispatched">Dispatched</option>
                                  <option value="Delivered">Delivered</option>
                                  <option value="Cancelled">Cancelled</option>
                                </select>
                              </div>
                              
                              {/* Payment status selector */}
                              <div>
                                <label className="block text-[10px] text-gray-400 font-semibold mb-0.5 uppercase">Payment Status</label>
                                <select
                                  value={payment_status}
                                  onChange={(e) => handleUpdatePaymentStatus(order_id, e.target.value)}
                                  className={`w-32 px-2 py-1 text-xs border rounded-lg font-bold bg-white focus:outline-none focus:ring-1 focus:ring-red-900 ${
                                    payment_status === "Paid" ? "text-green-700" : "text-orange-700"
                                  }`}
                                >
                                  <option value="Pending">Pending</option>
                                  <option value="Paid">Paid</option>
                                </select>
                              </div>
                            </td>
                            <td className="p-4 text-center">
                              <button
                                onClick={() => handlePrintInvoice(order)}
                                className="bg-red-50 hover:bg-red-100 text-red-900 border border-red-200 p-2.5 rounded-xl transition text-sm inline-flex items-center gap-1.5 font-bold"
                                title="Print Invoice / Bill"
                              >
                                <FaPrint /> Print
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Delivery Regions Management Tab */}
        {activeTab === "delivery" && (
          <div className="space-y-8 animate-fade-in">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-red-950">Delivery Regions Configuration</h1>
                <p className="text-sm text-gray-500">Manage delivery states/regions and their minimum order amounts. These values are used in Cart and Quick Shop.</p>
              </div>
              <button
                onClick={() => { fetchDeliveryRegions(); }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-semibold px-5 py-2.5 rounded-xl border transition"
              >
                🔄 Load from Server
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Add Region Form */}
              <div className="lg:col-span-1">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
                  <h3 className="font-bold text-red-950 flex items-center gap-2">
                    🚚 Add New Delivery Region
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">State / Region Name</label>
                      <input
                        type="text"
                        value={newRegionName}
                        onChange={(e) => setNewRegionName(e.target.value)}
                        placeholder="e.g. Tamil Nadu, Delhi"
                        className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Minimum Order Amount (₹)</label>
                      <input
                        type="number"
                        value={newRegionAmount}
                        onChange={(e) => setNewRegionAmount(e.target.value)}
                        placeholder="e.g. 3000"
                        className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                      />
                    </div>
                    <button
                      onClick={handleAddRegion}
                      className="w-full bg-red-900 hover:bg-red-800 text-white py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1 transition shadow-md"
                    >
                      <FaPlus /> Add Region
                    </button>
                  </div>
                </div>

                {/* Save Button */}
                <div className="bg-gradient-to-r from-green-700 to-green-600 rounded-2xl p-6 text-white mt-4">
                  <h3 className="font-bold text-sm mb-2">💾 Save All Changes</h3>
                  <p className="text-xs text-green-100 mb-4">Click below to save all delivery regions to the database. Cart and Quick Shop will use these values immediately.</p>
                  <button
                    onClick={handleSaveRegions}
                    className="w-full bg-white text-green-800 font-bold py-2.5 rounded-xl text-sm hover:bg-green-50 transition shadow-md"
                  >
                    <FaCheck className="inline mr-1.5" /> Save All Regions
                  </button>
                </div>
              </div>

              {/* Regions List */}
              <div className="lg:col-span-2">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                  <h3 className="text-lg font-bold text-red-950 mb-6 flex items-center gap-2">
                    <FaList className="text-red-800" /> Current Delivery Regions ({Object.keys(deliveryRegions).length})
                  </h3>
                  
                  {Object.keys(deliveryRegions).length === 0 ? (
                    <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed">
                      <p className="text-sm text-gray-500 font-semibold">No delivery regions configured.</p>
                      <p className="text-xs text-gray-400">Add regions using the form on the left, then click "Save All Regions".</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {Object.entries(deliveryRegions)
                        .sort((a, b) => a[1] - b[1])
                        .map(([regionName, minAmount]) => (
                          <div key={regionName} className="flex items-center justify-between p-3.5 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 transition">
                            <div className="flex items-center gap-3">
                              <span className="text-lg">📍</span>
                              <div>
                                <p className="font-bold text-gray-800 text-sm">{regionName}</p>
                                <p className="text-xs text-gray-500">Min Order: <span className="font-bold text-green-700">₹{minAmount}</span></p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveRegion(regionName)}
                              className="text-red-400 hover:text-red-600 p-2 hover:bg-red-50 rounded-lg transition"
                              title="Remove Region"
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Edit Category Modal */}
      {editingProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <h3 className="font-bold text-lg text-red-950">Edit Product Category Name</h3>
            <form onSubmit={handleEditProduct} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Category Name</label>
                <input
                  type="text"
                  value={editingProduct.name}
                  onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditingProduct(null)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-900 hover:bg-red-800 text-white rounded-xl text-sm font-semibold transition"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Subproduct Modal */}
      {editingSubproduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <h3 className="font-bold text-lg text-red-950">Edit Subproduct Item</h3>
            <form onSubmit={handleEditSubproductSubmit} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Subproduct Name</label>
                <input
                  type="text"
                  value={editingSubproduct.subName}
                  onChange={(e) => setEditingSubproduct({ ...editingSubproduct, subName: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Dazzle/Trend rating (0-5)</label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  value={editingSubproduct.trend}
                  onChange={(e) => setEditingSubproduct({ ...editingSubproduct, trend: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Product Image (Leave empty to keep current)</label>
                {editingSubproduct.childimg && (
                  <div className="mb-2">
                    <p className="text-[10px] text-gray-400 mb-1">Current Image:</p>
                    <img src={editingSubproduct.childimg} alt="Current" className="w-16 h-16 object-cover rounded-lg border" />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setEditingSubproduct({ ...editingSubproduct, newChildimg: e.target.files[0] })}
                  className="w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-red-50 file:text-red-900 hover:file:bg-red-100"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditingSubproduct(null)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-900 hover:bg-red-800 text-white rounded-xl text-sm font-semibold transition"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Rate Modal */}
      {editingRate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <h3 className="font-bold text-lg text-red-950">Edit Pricing & Rate Details</h3>
            <form onSubmit={handleEditRateSubmit} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Packaging Details</label>
                <input
                  type="text"
                  value={editingRate.quantity}
                  onChange={(e) => setEditingRate({ ...editingRate, quantity: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Price (₹)</label>
                  <input
                    type="text"
                    value={editingRate.rate}
                    onChange={(e) => setEditingRate({ ...editingRate, rate: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Discount (%)</label>
                  <input
                    type="text"
                    value={editingRate.Discount}
                    onChange={(e) => setEditingRate({ ...editingRate, Discount: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Stock Level</label>
                <input
                  type="number"
                  value={editingRate.stocks}
                  onChange={(e) => setEditingRate({ ...editingRate, stocks: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditingRate(null)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-900 hover:bg-red-800 text-white rounded-xl text-sm font-semibold transition"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Banner Modal */}
      {editingBanner && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <h3 className="font-bold text-lg text-red-950">Edit Homepage Banner Slide</h3>
            <form onSubmit={handleEditBannerSubmit} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Banner Title *</label>
                <input
                  type="text"
                  value={editingBanner.title}
                  onChange={(e) => setEditingBanner({ ...editingBanner, title: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Description / Subtitle</label>
                <textarea
                  value={editingBanner.description}
                  onChange={(e) => setEditingBanner({ ...editingBanner, description: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900 h-20"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Banner Slide Image (Leave empty to keep current)</label>
                {editingBanner.image_url && (
                  <div className="mb-2">
                    <p className="text-[10px] text-gray-400 mb-1">Current Image:</p>
                    <img src={editingBanner.image_url} alt="Current Banner" className="w-full aspect-video object-cover rounded-lg border" />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setEditingBanner({ ...editingBanner, newImage: e.target.files[0] })}
                  className="w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-red-50 file:text-red-900 hover:file:bg-red-100"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Active Visibility Status</label>
                <select
                  value={editingBanner.active}
                  onChange={(e) => setEditingBanner({ ...editingBanner, active: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-900 bg-white"
                >
                  <option value="1">Active (Show on homepage)</option>
                  <option value="0">Inactive (Hide)</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditingBanner(null)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-900 hover:bg-red-800 text-white rounded-xl text-sm font-semibold transition"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;